/* eslint-disable complexity */

interface HRDetConfig {
  fs: number; // Hz (np. 320)
  hpHz: number; // 5 Hz
  lpHz: number; // 18 Hz
  mwiMs: number; // 100 ms
  thrK: number; // 0.22
  refractMs: number; // 360 ms
  bootSec: number; // 0.5 s
  earlySec: number; // 8 s
  earlyRatio: number; // 2.2
}

interface BeatEventDetail {
  tSec: number;
  amp: number;
  rr?: number;
  bpmNow?: number;
}

export class ECGHeartRateDetector extends EventTarget {
  public readonly cfg: HRDetConfig;

  // filtry (HP/LP dla toru detekcji)
  private A_HP = 0;
  private A_LP = 0;
  private hpY = 0;
  private hpPrevX = 0;
  private lpY = 0;
  private prev = 0;

  // całka ruchoma (MWI)
  private mwiBuf: number[] = [];
  private mwiSum = 0;
  private mwiLen = 32;

  // segment bieżącego impulsu
  private inPulse = false;
  private segMax = 0;
  private segStartT = 0;
  private segPeakT = 0;

  // adaptacyjny próg (wg Pan–Tompkins)
  private SPKI = 0; // tracker „sygnału”
  private NPKI = 0; // tracker „szumu”
  private THRESH = 0; // próg
  private MIN_THR = 1e-9; // dolna szyna progu (powoli adaptowana)

  // HR/metyka czasu
  private tSec = 0;
  private lastBeatT = -1e9;
  private lastAmp = 0;
  private lastBeatWallMs = -1e12;

  private beats10s: number[] = [];
  private beats60s: number[] = [];
  private bpmNow = NaN;
  private bpmAvg = NaN;

  constructor(cfg?: Partial<HRDetConfig>) {
    super();
    this.cfg = Object.assign(
      {
        fs: 320,
        hpHz: 5,
        lpHz: 18,
        mwiMs: 100,
        thrK: 0.22,
        refractMs: 360,
        bootSec: 0.5,
        earlySec: 8.0,
        earlyRatio: 2.2,
      } as HRDetConfig,
      cfg || {},
    );
    this.recalcCoeffs();
    this.reset();
  }

  public reset() {
    this.hpY = 0;
    this.hpPrevX = 0;
    this.lpY = 0;
    this.prev = 0;

    this.mwiBuf.length = 0;
    this.mwiSum = 0;

    this.inPulse = false;
    this.segMax = 0;
    this.segStartT = 0;
    this.segPeakT = 0;

    this.SPKI = 0;
    this.NPKI = 0;
    this.THRESH = 0;
    this.MIN_THR = 1e-9;

    this.tSec = 0;
    this.lastBeatT = -1e9;
    this.lastAmp = 0;
    this.lastBeatWallMs = -1e12;

    this.beats10s.length = 0;
    this.beats60s.length = 0;
    this.bpmNow = NaN;
    this.bpmAvg = NaN;
  }

  private recalcCoeffs() {
    const Fs = this.cfg.fs;

    // 1-rz high-pass: y[n] = a*(y[n-1] + x[n] - x[n-1])
    const RCHp = 1 / (2 * Math.PI * this.cfg.hpHz);
    this.A_HP = RCHp / (RCHp + 1 / Fs);

    // 1-rz low-pass: y[n] = y[n-1] + a*(x[n] - y[n-1])
    const wLp = 2 * Math.PI * this.cfg.lpHz;
    this.A_LP = wLp / (wLp + Fs);

    this.mwiLen = Math.max(8, Math.round((this.cfg.mwiMs / 1000) * Fs));
    this.mwiBuf.length = 0;
    this.mwiSum = 0;

    this.inPulse = false;
    this.segMax = 0;
    this.segStartT = 0;
    this.segPeakT = 0;
  }

  /**
   * Podaj próbkę sygnału po odjęciu baseline (x0).
   * Jeśli nie podasz tSec, zostanie zinkrementowany o 1/Fs.
   */
  public stepSample(x0: number, tSec?: number) {
    const Fs = this.cfg.fs;
    if (tSec === undefined) {
      this.tSec += 1 / Fs;
    } else {
      this.tSec = tSec;
    }

    // 5–18 Hz: HP->LP
    const hp = (this.hpY = this.A_HP * (this.hpY + x0 - this.hpPrevX));
    this.hpPrevX = x0;
    const bp = (this.lpY = this.lpY + this.A_LP * (hp - this.lpY));

    // pochodna (dyskretna), kwadrat — energia QRS
    const der = (bp - this.prev) * Fs;
    this.prev = bp;
    const sq = der * der;

    // MWI
    this.mwiBuf.push(sq);
    this.mwiSum += sq;
    if (this.mwiBuf.length > this.mwiLen) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.mwiSum -= this.mwiBuf.shift()!;
    }
    const mwi = this.mwiSum / (this.mwiBuf.length || 1);

    // wolny tracker „tła” — dolna szyna progu
    this.MIN_THR = 0.9995 * this.MIN_THR + 0.0005 * mwi;

    // rozruch — karm tylko NPKI (szum); ustaw próg minimalny
    if (this.tSec < this.cfg.bootSec) {
      this.NPKI = 0.9 * this.NPKI + 0.1 * mwi;
      this.THRESH = Math.max(
        this.NPKI * (1 + this.cfg.thrK),
        1.5 * this.MIN_THR,
      );
      return;
    }

    // wejście do impulsu
    if (!this.inPulse && mwi > this.THRESH) {
      this.inPulse = true;
      this.segStartT = this.tSec;
      this.segPeakT = this.tSec;
      this.segMax = mwi;
    }

    // aktualizacja maksimum w impulsie
    if (this.inPulse) {
      if (mwi > this.segMax) {
        this.segMax = mwi;
        this.segPeakT = this.tSec;
      }
    }

    // wyjście z impulsu: spadek poniżej progu lub zbyt szerokie okno
    if (
      this.inPulse &&
      (mwi < this.THRESH || this.tSec - this.segStartT > 0.3)
    ) {
      const width = this.tSec - this.segStartT; // 40–220 ms ~ QRS
      const dt = this.segPeakT - this.lastBeatT;
      const refrDyn = Math.max(this.cfg.refractMs / 1000, 0.3); // twarde minimum

      let accept = false;
      if (width >= 0.04 && width <= 0.22) {
        if (dt >= refrDyn) {
          accept = true;
        } else if (dt >= 0.44 && this.segMax >= 0.8 * this.lastAmp) {
          // dopuszczenie dubletów/bigeminia – drugi wysoki pik po dłuższej przerwie
          accept = true;
        }
      }

      // wczesna faza zapoznawcza — wymagaj wyraźnie większej energii
      if (accept && this.tSec < this.cfg.earlySec) {
        if (!(this.segMax > this.cfg.earlyRatio * this.THRESH)) {
          accept = false;
        }
      }

      if (accept) {
        // zarejestruj uderzenie
        this.registerBeat(this.segPeakT, this.segMax);
        // aktualizuj tracker „sygnału”
        this.SPKI = 0.875 * this.SPKI + 0.125 * this.segMax;
      } else {
        // aktualizuj tracker „szumu”
        this.NPKI = 0.875 * this.NPKI + 0.125 * this.segMax;
      }

      // próg adaptacyjny
      this.THRESH = this.NPKI + this.cfg.thrK * (this.SPKI - this.NPKI);
      this.THRESH = Math.max(this.THRESH, 1.5 * this.MIN_THR);

      // reset segmentu
      this.inPulse = false;
      this.segMax = 0;
    }
  }

  private registerBeat(t: number, amp: number) {
    // natychmiastowy HR po 2. biciu
    if (this.lastBeatT > -1e8) {
      const rr = t - this.lastBeatT;
      if (rr > 0.2 && rr < 2.0) {
        this.bpmNow = 60 / rr;
      }
    }

    // ściana czasu do sygnałów UI
    this.lastBeatWallMs = performance.now();

    // bufory okienne
    this.beats10s.push(t);
    while (this.beats10s.length && t - this.beats10s[0] > 10) {
      this.beats10s.shift();
    }

    this.beats60s.push(t);
    while (this.beats60s.length && t - this.beats60s[0] > 60) {
      this.beats60s.shift();
    }

    // HR bardziej stabilny (mediana RR)
    if (this.beats10s.length >= 3) {
      const rr: number[] = [];
      for (let i = 1; i < this.beats10s.length; i++) {
        rr.push(this.beats10s[i] - this.beats10s[i - 1]);
      }
      const rrMed = this.median(rr);
      if (Number.isFinite(rrMed) && rrMed > 0) {
        this.bpmNow = 60 / rrMed;
      }
    }
    if (this.beats60s.length >= 3) {
      const rr2: number[] = [];
      for (let i = 1; i < this.beats60s.length; i++) {
        rr2.push(this.beats60s[i] - this.beats60s[i - 1]);
      }
      const rrMed2 = this.median(rr2);
      if (Number.isFinite(rrMed2) && rrMed2 > 0) {
        this.bpmAvg = 60 / rrMed2;
      }
    }

    this.lastBeatT = t;
    this.lastAmp = amp;

    // event dla UI/telemetrii
    this.dispatchEvent(
      new CustomEvent<BeatEventDetail>('beat', {
        detail: { tSec: t, amp, rr: undefined, bpmNow: this.bpmNow },
      }),
    );
  }

  private median(a: number[]): number {
    if (!a || !a.length) {
      return NaN;
    }
    const b = a.slice().sort((x, y) => x - y);
    const n = b.length;
    return n % 2 ? b[(n - 1) / 2] : 0.5 * (b[n / 2 - 1] + b[n / 2]);
  }

  /** Bieżący HR do UI (zabezpieczenie świeżości i streamingu zostawiam hostowi) */
  public getBpm(): number | null {
    return Number.isFinite(this.bpmNow) ? Math.round(this.bpmNow) : null;
  }

  /** Czy „przed chwilą” był beat – do animacji serduszka w UI */
  public beatJustNow(windowMs = 140): boolean {
    return performance.now() - this.lastBeatWallMs <= windowMs;
  }
}
